import { useState, useRef, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { MapPin, Send, Radio, Zap } from "lucide-react";

const FREQ_PRESETS = [
  { hz: 7.83, label: "Schumann", effect: "Earth Resonance" },
  { hz: 174, label: "174 Hz", effect: "Pain Relief" },
  { hz: 285, label: "285 Hz", effect: "Tissue Healing" },
  { hz: 396, label: "396 Hz", effect: "Liberation" },
  { hz: 417, label: "417 Hz", effect: "Change" },
  { hz: 432, label: "432 Hz", effect: "Universal Harmony" },
  { hz: 528, label: "528 Hz", effect: "Love / DNA Repair" },
  { hz: 639, label: "639 Hz", effect: "Connection" },
  { hz: 741, label: "741 Hz", effect: "Intuition" },
  { hz: 852, label: "852 Hz", effect: "Spiritual Order" },
  { hz: 963, label: "963 Hz", effect: "Divine Consciousness" },
];

const MOCK_CONTACTS = [
  { id: "mirrors-707a", name: "MIRRORS-707A", lastFreq: "432 Hz" },
  { id: "mirrors-13b2", name: "MIRRORS-13B2", lastFreq: "528 Hz" },
  { id: "mirrors-96f0", name: "MIRRORS-96F0", lastFreq: "96 Hz" },
];

export default function GodmodePage() {
  const [query, setQuery] = useState("");
  const [mapSrc, setMapSrc] = useState(
    "https://maps.google.com/maps?q=40.7831,-73.9712&z=12&t=k&output=embed"
  );
  const [selectedFreq, setSelectedFreq] = useState(FREQ_PRESETS[5]); // 432 Hz
  const [customHz, setCustomHz] = useState("");
  const [targetContact, setTargetContact] = useState(null);
  const [targetLocation, setTargetLocation] = useState("");
  const [transmissions, setTransmissions] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef(null);

  const search = () => {
    const q = query.trim();
    if (!q) return;
    setMapSrc(
      `https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=14&t=k&output=embed`
    );
    setTargetLocation(q);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") search();
  };

  const getFreqEffect = (hz) => {
    const preset = FREQ_PRESETS.find((p) => p.hz === hz);
    return preset?.effect || "Custom Frequency";
  };

  const sendFrequency = () => {
    const hz = customHz ? Number(customHz) : selectedFreq.hz;
    if (!hz || hz <= 0) {
      toast.error("Select a valid frequency");
      return;
    }
    const target = targetContact?.name || targetLocation || "Current Location";
    const effect = getFreqEffect(hz);

    setIsSending(true);
    setTimeout(() => setIsSending(false), 2000);

    const tx = {
      id: Date.now(),
      hz,
      effect,
      target,
      timestamp: new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
      active: true,
    };
    setTransmissions((prev) => [tx, ...prev].slice(0, 12));
    toast.success(`Transmitting ${hz} Hz → ${target}`);

    // Auto-expire after 30s
    setTimeout(() => {
      setTransmissions((prev) =>
        prev.map((t) => (t.id === tx.id ? { ...t, active: false } : t))
      );
    }, 30000);
  };

  const activeCount = transmissions.filter((t) => t.active).length;

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-6 py-4 space-y-5">
      <div>
        <h1
          className="text-2xl sm:text-3xl font-mono font-bold text-[#FF0000] tracking-widest"
          data-testid="godmode-page-title"
        >
          GODMODE
        </h1>
        <p className="text-[10px] sm:text-xs font-mono tracking-widest text-[#666] uppercase">
          Resonance Network &#183; Send Frequencies &#183; Interactive
          Earth
        </p>
      </div>

      {/* Map + Search */}
      <Card
        className="sg-frame border-[#FF0000]/20 overflow-hidden"
        data-testid="godmode-map-card"
      >
        <CardContent className="p-0">
          {/* Search */}
          <div
            className="flex gap-2 p-3 border-b border-[#FF0000]/15 bg-black"
            data-testid="godmode-map-search"
          >
            <MapPin className="w-4 h-4 text-[#FF0000]/60 mt-1 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Search any location..."
              className="flex-1 bg-transparent border-none text-[#FF0000] text-xs font-mono tracking-wider placeholder:text-[#444] focus:outline-none"
              data-testid="godmode-search-input"
            />
            <button
              onClick={search}
              className="px-4 py-1 text-[9px] tracking-widest uppercase font-mono border border-[#FF0000]/40 text-[#FF0000] hover:bg-[#FF0000]/15 transition-colors"
              data-testid="godmode-search-btn"
            >
              GO
            </button>
          </div>

          {/* Map with overlay */}
          <div
            className="relative"
            style={{ height: "380px" }}
            data-testid="godmode-map-container"
          >
            <iframe
              key={mapSrc}
              src={mapSrc}
              title="GODMODE Earth"
              className="w-full h-full border-0"
              style={{ display: "block" }}
              allow="fullscreen"
              loading="lazy"
              referrerPolicy="no-referrer"
            />

            {/* Frequency transmission overlay */}
            {activeCount > 0 && (
              <div className="absolute inset-0 pointer-events-none z-10">
                {/* Pulse rings */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  {[1, 2, 3].map((ring) => (
                    <div
                      key={ring}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#FF0000]/40"
                      style={{
                        width: `${60 + ring * 50}px`,
                        height: `${60 + ring * 50}px`,
                        animation: `godmode-pulse ${2 + ring * 0.5}s ease-out infinite`,
                        animationDelay: `${ring * 0.3}s`,
                      }}
                    />
                  ))}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#FF0000] shadow-[0_0_15px_rgba(255,0,0,0.6)]" />
                </div>

                {/* Active frequency badge */}
                <div className="absolute top-3 left-3 bg-black/85 border border-[#FF0000]/40 px-3 py-1.5 space-y-1">
                  <div className="flex items-center gap-2">
                    <Radio className="w-3 h-3 text-[#FF0000] animate-pulse" />
                    <span className="text-[9px] font-mono tracking-widest text-[#FF0000]">
                      {activeCount} ACTIVE TRANSMISSION{activeCount > 1 ? "S" : ""}
                    </span>
                  </div>
                  {transmissions
                    .filter((t) => t.active)
                    .slice(0, 3)
                    .map((tx) => (
                      <div
                        key={tx.id}
                        className="text-[8px] font-mono text-[#FF0000]/70"
                      >
                        {tx.hz} Hz &#8594; {tx.target} &#183; {tx.effect}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* LIVE indicator */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/70 px-2 py-1 border border-[#FF0000]/30">
              <div className="w-1.5 h-1.5 rounded-full bg-[#FF0000] animate-pulse" />
              <span className="text-[8px] font-mono tracking-widest text-[#FF0000]/70">
                LIVE
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transmit Panel */}
      <Card
        className="sg-frame border-[#FF0000]/20"
        data-testid="godmode-transmit-card"
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-[#FF0000] text-sm tracking-widest font-mono flex items-center gap-2">
            <Zap className="w-4 h-4" />
            TRANSMIT FREQUENCY
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Frequency selector grid */}
          <div className="space-y-2">
            <div className="text-[9px] font-mono tracking-widest text-[#666] uppercase">
              Select Frequency
            </div>
            <div
              className="flex flex-wrap gap-1.5"
              data-testid="godmode-freq-grid"
            >
              {FREQ_PRESETS.map((preset) => (
                <button
                  key={preset.hz}
                  onClick={() => {
                    setSelectedFreq(preset);
                    setCustomHz("");
                  }}
                  className={`px-2 py-1 text-[9px] font-mono tracking-wider border transition-all ${
                    selectedFreq.hz === preset.hz && !customHz
                      ? "border-[#FF0000] bg-[#FF0000]/15 text-[#FF0000] shadow-[0_0_8px_rgba(255,0,0,0.2)]"
                      : "border-[#FF0000]/20 text-[#FF0000]/50 hover:border-[#FF0000]/50 hover:text-[#FF0000]/80"
                  }`}
                  data-testid={`godmode-freq-${preset.hz}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={customHz}
                onChange={(e) => setCustomHz(e.target.value)}
                placeholder="Custom Hz..."
                className="flex-1 bg-black border border-[#FF0000]/20 text-[#FF0000] text-xs font-mono px-3 py-1.5 placeholder:text-[#444] focus:border-[#FF0000]/50 focus:outline-none"
                data-testid="godmode-custom-hz"
              />
              <div className="text-[9px] font-mono text-[#666]">
                {customHz
                  ? `${customHz} Hz`
                  : <>{selectedFreq.hz} Hz &middot; {selectedFreq.effect}</>}
              </div>
            </div>
          </div>

          {/* Target selector */}
          <div className="space-y-2">
            <div className="text-[9px] font-mono tracking-widest text-[#666] uppercase">
              Target &#183;{" "}
              {targetContact
                ? targetContact.name
                : targetLocation || "Current Map Location"}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setTargetContact(null)}
                className={`px-2 py-1 text-[9px] font-mono tracking-wider border transition-all ${
                  !targetContact
                    ? "border-[#FF0000] bg-[#FF0000]/15 text-[#FF0000]"
                    : "border-[#FF0000]/20 text-[#FF0000]/50 hover:text-[#FF0000]/80"
                }`}
                data-testid="godmode-target-location"
              >
                <MapPin className="w-3 h-3 inline mr-1" />
                MAP LOCATION
              </button>
              {MOCK_CONTACTS.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => setTargetContact(contact)}
                  className={`px-2 py-1 text-[9px] font-mono tracking-wider border transition-all ${
                    targetContact?.id === contact.id
                      ? "border-[#FF0000] bg-[#FF0000]/15 text-[#FF0000]"
                      : "border-[#FF0000]/20 text-[#FF0000]/50 hover:text-[#FF0000]/80"
                  }`}
                  data-testid={`godmode-target-${contact.id}`}
                >
                  {contact.name}
                </button>
              ))}
            </div>
          </div>

          {/* Send button */}
          <Button
            onClick={sendFrequency}
            disabled={isSending}
            className={`w-full font-mono tracking-widest text-sm transition-all ${
              isSending
                ? "bg-[#FF0000]/50 text-black"
                : "bg-[#FF0000] text-black hover:bg-[#FF3333]"
            }`}
            data-testid="godmode-send-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {isSending ? "TRANSMITTING..." : "SEND FREQUENCY"}
          </Button>
        </CardContent>
      </Card>

      {/* Active Transmissions Log */}
      {transmissions.length > 0 && (
        <Card
          className="sg-frame border-[#FF0000]/20"
          data-testid="godmode-tx-log"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-[#FF0000] text-sm tracking-widest font-mono flex items-center gap-2">
              <Radio className="w-4 h-4" />
              TRANSMISSION LOG
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {transmissions.map((tx) => (
                <div
                  key={tx.id}
                  className={`flex items-center justify-between gap-3 p-2 border font-mono text-[10px] transition-all ${
                    tx.active
                      ? "border-[#FF0000]/40 bg-[#FF0000]/5"
                      : "border-[#333]/30 bg-black/50 opacity-50"
                  }`}
                  data-testid={`godmode-tx-${tx.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        tx.active
                          ? "bg-[#FF0000] animate-pulse"
                          : "bg-[#444]"
                      }`}
                    />
                    <span className="text-[#FF0000] font-bold">
                      {tx.hz} Hz
                    </span>
                    <span className="text-[#666]">&#8594;</span>
                    <span className="text-[#888] truncate">{tx.target}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[#FF0000]/60">{tx.effect}</span>
                    <span className="text-[#444]">{tx.timestamp}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
